import {
    AMOUNT_OF_DATES_FOR_STATS,
    Button,
    DATE_FORMAT,
    DAY_FORMAT,
    DAY_LONG_FORMAT,
    DeviceUtils,
    RestaurantUtils,
    SERVER_DATE_FORMAT,
} from '@localina/core';
import { DateTime } from 'luxon';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import SwipeableViews from 'react-swipeable-views';
import { useRestaurant } from '../../api/queries/restaurants';
import { useRestaurantStats } from '../../api/queries/restaurantStats';
import { Page, RestaurantDateStats, RestaurantStatsWeekStrip } from '../../components';
import { Path } from '../../enums';
import { PathUtils } from '../../utils';

const AMOUNT_OF_PREVIOUS_DATES_FOR_STATS = 2;

const Dashboard: React.FC = () => {
    const navigate = useNavigate();
    const { i18n, t } = useTranslation();

    const [index, setIndex] = React.useState(0);
    const didSetInitialIndex = React.useRef(false);

    const restaurantQuery = useRestaurant();

    const previousDates: string[] =
        restaurantQuery.isSuccess && restaurantQuery.data
            ? RestaurantUtils.getPreviousOpeningDates(restaurantQuery.data, AMOUNT_OF_PREVIOUS_DATES_FOR_STATS)
            : [];
    const nextDates: string[] =
        restaurantQuery.isSuccess && restaurantQuery.data
            ? RestaurantUtils.getNextOpeningDates(restaurantQuery.data, AMOUNT_OF_DATES_FOR_STATS)
            : [];
    const dates: string[] = [...previousDates, ...nextDates];

    React.useEffect(() => {
        if (!didSetInitialIndex.current && dates.length > 0) {
            setIndex(previousDates.length);
            didSetInitialIndex.current = true;
        }
    }, [dates.length, previousDates.length]);

    const restaurantStatsQuery = useRestaurantStats(dates, {
        enabled: Boolean(restaurantQuery.isSuccess && restaurantQuery.data),
    });

    const previousWeekDates: string[] = dates.map((date) =>
        DateTime.fromFormat(date, SERVER_DATE_FORMAT).minus({ days: 7 }).toFormat(SERVER_DATE_FORMAT),
    );
    const previousWeekStatsQuery = useRestaurantStats(previousWeekDates, {
        enabled: dates.length > 0,
    });

    const handleClick = (date: string) => {
        if (restaurantQuery.isSuccess && restaurantQuery.data) {
            navigate(
                PathUtils.generateUrl(
                    Path.RESTAURANT_RESERVATIONS,
                    { restaurantId: restaurantQuery.data.id },
                    { date },
                ),
            );
        }
    };

    const selectedDate = restaurantStatsQuery.data?.stats[index]?.date;
    const subtitle = selectedDate
        ? DateTime.fromFormat(selectedDate, SERVER_DATE_FORMAT).toFormat(
              `${DeviceUtils.isMobile() ? DAY_FORMAT : DAY_LONG_FORMAT}, ${DATE_FORMAT}`,
              { locale: i18n.language },
          )
        : undefined;

    const actions = (
        <>
            <Button
                label={t('core:dateField.buttons.today')}
                secondary={true}
                onClick={() => setIndex(previousDates.length)}
            />
            <Button
                label={t('dashboard.buttons.showReservations')}
                disabled={!selectedDate}
                onClick={() => selectedDate && handleClick(selectedDate)}
            />
        </>
    );

    return (
        <Page
            name="dashboard"
            title={{ value: t('dashboard.title'), subtitle, actions }}
            isLoading={restaurantStatsQuery.isLoading}
        >
            {restaurantStatsQuery.isSuccess && restaurantStatsQuery.data.stats.length !== 0 && (
                <>
                    <RestaurantStatsWeekStrip
                        stats={restaurantStatsQuery.data.stats}
                        activeIndex={index}
                        onChange={setIndex}
                    />
                    <SwipeableViews index={index} onChangeIndex={setIndex}>
                        {restaurantStatsQuery.data.stats.map((stats, idx) => (
                            <RestaurantDateStats
                                key={idx}
                                stats={stats}
                                previousWeekStats={previousWeekStatsQuery.data?.stats[idx]?.occupancyStats}
                            />
                        ))}
                    </SwipeableViews>
                </>
            )}
        </Page>
    );
};

export default Dashboard;
